import { ref, onBeforeUpdate, nextTick, computed } from 'vue'
import { EDirections } from '@/models/enums'
import { TEmit } from '@/types/global'
import { TOption, TSelect, TSelectProps } from '@/components/select/types'
import { getArrayIndexByDuration } from '@/components/select/helpers'
import {
  CLASS_SELECTED_OPTION,
  SEARCH_BOX_HEIGHT,
} from '@/components/select/constants'

export const useSelect = (
  props: TSelectProps,
  emit: TEmit,
  emitHandlers?: {
    handleModelChange: (
      value: unknown,
      model: unknown,
      label: string,
      groupId: string | null,
    ) => void
    handleGroupChange: (groupId: string, value: unknown, model: unknown) => void
    handleLabelChange: (label: string) => void
  },
): TSelect => {
  const indexFocusedOption = ref(-1)
  const optionsRefs = ref<HTMLElement[]>([])
  const selectListRef = ref<HTMLElement | undefined>(undefined)
  let selectedOptionIndex = -1
  const show = ref(false)

  onBeforeUpdate(() => (optionsRefs.value = []))

  const scrollToSelectedOption = () => {
    const selectedOption: HTMLElement | null =
      optionsRefs.value[selectedOptionIndex]
    if (selectListRef.value !== undefined)
      selectListRef.value.scrollTop =
        selectedOption?.offsetTop - SEARCH_BOX_HEIGHT
  }

  const getOptionName = (option: TOption) =>
    typeof option === 'object'
      ? option[props.labelBy]
        ? option[props.labelBy]
        : option.name
      : option

  const getOptionValue = (option: TOption) =>
    typeof option === 'object' ? option[props.trackBy] : option

  const setOptionRef = (el: HTMLElement, i: number) => {
    if (el) {
      optionsRefs.value[i] = el
    }
  }

  const setSelectListRef = (dropdown: HTMLElement) => {
    if (dropdown) {
      selectListRef.value = dropdown
    }
  }

  const setOpen = (state = false) => {
    show.value = state
  }

  const toggleDropdown = async () => {
    if (props.disabled) {
      setOpen(false)
      return
    }
    if (show.value) {
      setOpen(false)
    } else {
      unfocusOption()
      setOpen(true)

      await nextTick()
      if (props.modelValue) scrollToSelectedOption()
    }
  }

  const selectOption = (optionIndex: number, groupId: string | null = null) => {
    const option = props.options[optionIndex]
    selectedOptionIndex = optionIndex
    indexFocusedOption.value = optionIndex
    const value = typeof option === 'object' ? option[props.trackBy] : option
    const label = typeof option === 'object' ? option[props.labelBy] : option
    const group = String(groupId)
    emit('update:model-value', value, option)
    if (emitHandlers) {
      emitHandlers.handleModelChange(value, option, label, group)
      emitHandlers.handleGroupChange(group, value, option)
    }
  }

  const unfocusOption = () => {
    if (indexFocusedOption.value === -1) return
    indexFocusedOption.value = -1
  }

  const handleKey = async (duration: EDirections) => {
    if (!show.value || !props.options.length) return
    indexFocusedOption.value = getArrayIndexByDuration({
      duration,
      array: props.options as [],
      curIndex: indexFocusedOption.value,
    })
    await nextTick()
    const selectedEl = optionsRefs.value.find((r) =>
      r.className.includes(CLASS_SELECTED_OPTION),
    )
    selectedEl?.scrollIntoView({ block: 'nearest', inline: 'start' })
  }

  const handleEnterKey = () => {
    if (!show.value || indexFocusedOption.value === -1) {
      toggleDropdown()
      return
    }
    selectOption(indexFocusedOption.value)
    setOpen(false)
  }

  const wrappedValue = computed(() => {
    const _modelValue = props.modelValue
    const result: TOption = { name: '', [props.trackBy]: '' }
    if (_modelValue === null) {
      return result
    }

    if (typeof _modelValue === 'object') {
      return _modelValue as TOption
    }
    const selectedOption = props.options.find(
      (x) => x[props.trackBy] === _modelValue,
    )
    result.name =
      typeof selectedOption === 'object'
        ? props.labelBy
          ? selectedOption[props.labelBy]
          : selectedOption.name
        : _modelValue
    result[props.trackBy] = _modelValue
    return result
  })

  return {
    wrappedValue,
    getOptionValue,
    getOptionName,
    setOptionRef,
    indexFocusedOption,
    optionsRefs,
    show,
    handleEnterKey,
    setOpen,
    toggleDropdown,
    selectOption,
    setSelectListRef,
    handleKey,
  }
}
